/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import ICONS from "../../../../assets/Icons";
import PrimaryStepper from "../../../../components/_utils/CommonSteppers/PrimaryStepper";
import SVGComponent from "../../../../components/_utils/SVGComponent";
import RemitAccountStepName from "../../../../components/Accounts/RemitAccount/RemitAccountCreate/RemitAccoountStepName";
import RemitAccountStepConfirm from "../../../../components/Accounts/RemitAccount/RemitAccountCreate/RemitAccountStepConfirm";
import {
  remitterAccountCreate,
  remitterAccountViewAll,
  resetCreateRemitter,
} from "../../../../redux/features/accounts/remitterAccount/remitterAccount";
import CommonToast from "../../../../components/_utils/CommonToast";

const RemitterAccountCreate = () => {
  const scrollIt = useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [direction, setDirection] = useState(null);
  const [labelpopup, setLabelpopup] = useState(false);
  const [isDuplicate, setIsDuplicate] = useState(false);
  const [isNextDisabled, setIsNextDisabled] = useState(true);
  const [createDisabled, setCreateDisabled] = useState(false);
  const [removeClassName, setRemoveClassName] = useState(true);
  const [duplicateLoading, setDuplicateLoading] = useState(false);
  const [openSimilarPopup, setOpenSimilarPopup] = useState(false);
  const [accountPayLoad, setAccountPayLoad] = useState({
    remitterAccountName: "",
    labels: [],
  });

  const animationClass =
    direction === "next" ? "animate-slide-next" : "animate-slide-prev";

  const steps = ["Account name and labels", "Confirm and create"];

  const commonStepperConfig = {
    previousEnableBgColor: "var(--primary-nav-act)",
    previousDisableBgColor: "var(--text-disabled)",
    previousEnableTextColor: "var(--text-primary)",
    previousHoverOption: "white",
    nextEnableBgColor: "var(--text-primary)",
    nextDisableBgColor: "var(--text-disabled)",
    nextEnableTextColor: "var(--primary-bg)",
    nextDisableTextColor: "var(--text-primary)",
    nextHoverOption: "black",
  };

  const dataArray = steps.map((stepLabel, index) => {
    const isLastStep = index === steps.length - 1;
    const isRemitterAccountLoading = isLastStep && createDisabled;
    return {
      ...commonStepperConfig,
      previousText: index === 0 ? "Cancel" : "Previous",
      previousIsDisabled: isRemitterAccountLoading,
      nextText: isLastStep ? "Create" : "Next",
      nextLoaderNeeded: isLastStep,
      nextIsLoading: isRemitterAccountLoading,
      nextIsEnabled: isLastStep ? !createDisabled : !isNextDisabled,
      nextEnableBgColor: isLastStep
        ? "var(--positive-act)"
        : "var(--text-primary)",
      nextDisableBgColor: isLastStep
        ? "var(--positive-act)"
        : "var(--text-disabled)",
      nextEnableTextColor: isLastStep
        ? "var(--text-primary)"
        : "var(--primary-bg)",
      nextDisableTextColor: isLastStep
        ? "var(--text-primary)"
        : "var(--text-primary)",
      stepperText: stepLabel,
    };
  });

  const { services, isLoading } = useSelector((state) => state.refreshToken);

  const handleCreateAccount = async () => {
    try {
      setCreateDisabled(true);

      const data = await dispatch(
        remitterAccountCreate({
          name: accountPayLoad.remitterAccountName,
          labels: accountPayLoad.labels,
        }),
      ).unwrap();

      const remitterAccountId = data?.remitter_accounts?.[0]?.id;

      if (remitterAccountId) {
        navigate(
          `/remitter_account_dashboard/create_remitter_account/success/${remitterAccountId}`,
        );
      }
    } catch (error) {
      console.error("Create account failed", error);
      navigate("/remitter_account_dashboard/create_remitter_account/failed", {
        state: error,
      });
    } finally {
      setCreateDisabled(false);
    }
  };

  const checkDuplicateAccount = async () => {
    const name = accountPayLoad?.remitterAccountName?.trim();

    if (!name) return false;

    try {
      setDuplicateLoading(true);

      const response = await dispatch(
        remitterAccountViewAll({
          search_string: name,
        }),
      );

      const duplicateFound =
        response?.payload?.remitter_accounts?.some(
          (item) => item?.name?.toLowerCase() === name.toLowerCase(),
        ) || false;

      setIsDuplicate(duplicateFound);
      if (duplicateFound) {
        CommonToast({
          text: "Account already exists with the same name.",
          icon: ICONS?.ERROR_WHITE_CIRCLE,
          variant: "var(--error)",
        });
      }
      return duplicateFound;
    } catch (error) {
      console.error(error);
      return false;
    } finally {
      setDuplicateLoading(false);
    }
  };

  const handleNext = async () => {
    const isLastStep = activeStep === steps.length - 1;

    if (isLastStep) {
      handleCreateAccount();
      return;
    }

    if (activeStep === 0) {
      const duplicateFound = await checkDuplicateAccount();

      if (duplicateFound) {
        setOpenSimilarPopup(false);
        return;
      }
    }

    setActiveStep((prev) => prev + 1);
    setDirection("next");

    setRemoveClassName(false);

    setTimeout(() => {
      setRemoveClassName(true);
    }, 1000);
  };

  const handleBack = () => {
    if (activeStep === 0) {
      navigate(-1);
      return;
    }
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
    setDirection("previous");
    setRemoveClassName(false);
    setTimeout(() => {
      setRemoveClassName(true);
    }, 1000);
  };

  const accountHome = () => {
    navigate("/remitter_account_dashboard");
  };

  const renderCreateAccount = () => {
    switch (activeStep) {
      case 0:
        return (
          <RemitAccountStepName
            accountPayLoad={accountPayLoad}
            setAccountPayLoad={setAccountPayLoad}
            labelpopup={labelpopup}
            setLabelpopup={setLabelpopup}
            setIsDuplicate={setIsDuplicate}
            openSimilarPopup={openSimilarPopup}
            setOpenSimilarPopup={setOpenSimilarPopup}
            duplicateLoading={duplicateLoading}
            setDuplicateLoading={setDuplicateLoading}
            checkDuplicateAccount={checkDuplicateAccount}
            isDuplicate={isDuplicate}
          />
        );
      case 1:
        return (
          <RemitAccountStepConfirm
            accountPayLoad={accountPayLoad}
            setAccountPayLoad={setAccountPayLoad}
          />
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    if (!isLoading && services != null) {
      if (services?.includes("REUAccount") || services?.includes("Admin")) {
      } else {
        navigate("/feature_not_assigned", {
          state: { unavailableService: "REUAccount" },
        });
      }
    }
  }, [services, isLoading]);

  useEffect(() => {
    let isValid = false;

    if (activeStep === 0) {
      const name = accountPayLoad?.remitterAccountName || "";

      isValid = name.trim().length > 0 && !isDuplicate;
    }

    setIsNextDisabled(!isValid);
  }, [accountPayLoad, activeStep, isDuplicate]);

  useEffect(() => {
    return () => {
      dispatch(resetCreateRemitter());
    };
  }, [dispatch]);

  return (
    <div className="w-width-full flex flex-col items-center h-full-height bg-primary-bg">
      <div className="w-width-full Qlg:bg-transparent bg-primary-nav">
        <div className="h-navbar-height hidden Qsmd:block"></div>
        <div className="h-[calc(var(--navbar-breadcrumb-height)-var(--navbar-height))] mb-margin-sm mt-margin-xs Qsmd:my-margin-base"></div>
      </div>

      <div className="flex justify-center items-end !bottom-bottom-base rounded-t-border-radius-md Qlg:bg-transparent bg-primary-nav rounded-b-border-radius-md w-screen fixed Qsmd:relative Qsmd:top-top-base left-left-base z-z-index-header flex-shrink-0 h-[var(--padding-bottom-unassign-cat)]">
        <PrimaryStepper
          activeStep={activeStep}
          handleForwardAction={handleNext}
          handleBackwardAction={handleBack}
          dataArray={dataArray}
        />
      </div>

      <div
        className="flex items-center w-width-full bg-primary-nav p-padding-sm h-mobile-create-header fixed left-left-base top-top-base z-z-index-header rounded-b-border-radius-md Qsmd:hidden"
        onClick={accountHome}
      >
        <SVGComponent
          color="var(--text-primary)"
          height="var(--icon-sm)"
          width="var(--icon-sm)"
          url={ICONS?.ARROW_LEFT_WHITE}
          alt="Icon"
        />
        <div className=" flex flex-col pl-padding-md gap-gap-xs">
          <p className=" text-font-md font-sans text-text-primary">
            Remitter Account
          </p>
          <p className="text-xs font-sans text-text-primary">
            {steps[activeStep]}
          </p>
        </div>
      </div>

      <>
        <div
          className="overflow-x-hidden overflow-y-auto h-full flex flex-col items-center relative w-width-full"
          ref={scrollIt}
        >
          <div
            className={`w-width-full  Qsmd:pt-padding-md Qsmd:pb-padding-md pt-padding-base max-w-tab-width-normal mx-auto ${
              activeStep === 0 && direction === "next"
                ? ""
                : removeClassName === true
                  ? ""
                  : animationClass
            }`}
          >
            {renderCreateAccount()}
          </div>
        </div>
      </>

      <div className="h-temp-mobile-header flex-shrink-0 w-width-full Qsmd:hidden block"></div>
    </div>
  );
};

export default RemitterAccountCreate;
